import { type QueryKey, useQuery } from '@tanstack/react-query';

import type { ConfiguredCustomerType } from '@dutchie-pos/requests/checkin/typed-id-entry';

import { post } from 'src/utils/helpers/http';
import { useBaseAPIParams } from 'src/utils/hooks/useBaseAPIParams';

export const GetCustomerTypes: QueryKey = ['get-customer-types'];

const GET_CUSTOMER_TYPES_URL = '/posv3/maintenance/get-customer-types';

/**
 * A customer type as this location has it configured. The two flags are what classify it, and the
 * shared selector reads nothing else — so this response is typed as exactly what that selector
 * needs rather than as the whole row.
 */
export type CustomerTypeResponse = ConfiguredCustomerType & {
  CustomerType?: string;
};

/**
 * The location's own customer types.
 *
 * Fetched because the typed ID fallback has to name one, and which ids exist is per-dispensary
 * configuration: a medical-only location may run MMIC (10) as its only medical type, and sending an
 * id this location never configured creates a profile the back office cannot classify. Gated on
 * `enabled` so the request only happens on the screen that needs it.
 */
export function useCustomerTypesQuery({ enabled }: { enabled: boolean }) {
  const baseAPIParams = useBaseAPIParams();

  return useQuery({
    /**
     * Keyed by the location that answered, not just by the question.
     *
     * These ids are per-dispensary configuration, and this app switches location within a session -
     * the path chooser and the register chooser both come back. On a bare key the second location
     * reads the first one's cached list, and a typed entry then creates a profile with a
     * `CustomerTypeId` that location never configured. The same shape the sibling location query
     * uses for the same reason.
     */
    queryKey: [...GetCustomerTypes, baseAPIParams.LspId, baseAPIParams.LocId],
    queryFn: async () => await post<CustomerTypeResponse[]>(GET_CUSTOMER_TYPES_URL, baseAPIParams),
    enabled,
  });
}
